import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    const submitAddr = form.getAttribute('action') || '';

    try {
      const res = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || responseText
          || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于窝窝影院',
    description: '窝窝影院品牌介绍、口碑评价及联系方式。窝窝影院提供高清播放、在线不缓冲的流畅观影体验。',
    url: 'https://wowocinema.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '窝窝影院',
      description: '窝窝影院提供高清播放、在线不缓冲的流畅观影体验，超清画质无广告，海量电影电视剧免费在线观看。',
      url: 'https://wowocinema.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '窝窝影院是否真的免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，窝窝影院所有影片完全免费在线观看，无需注册、无需付费、无隐藏收费。我们依靠广告收入维持运营，为用户提供高清流畅的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '窝窝影院的画质如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '窝窝影院提供超清画质播放，支持1080P和4K分辨率。我们采用先进的视频压缩和CDN加速技术，确保在线播放不缓冲、不卡顿。',
        },
      },
      {
        '@type': 'Question',
        name: '窝窝影院每日更新多少部影片？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '窝窝影院每日更新最新上映的电影和电视剧，确保用户能够第一时间观看到最新内容。我们的内容团队全天候工作，为您精选优质影视资源。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在窝窝影院搜索想看的影片？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的影片分类导航浏览不同类型的内容，也可以使用搜索功能直接查找您想看的影片。我们提供了动作片、喜剧片、爱情片、科幻片等多种分类。',
        },
      },
      {
        '@type': 'Question',
        name: '窝窝影院支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '窝窝影院支持所有主流设备，包括电脑、平板和手机。我们的网站采用响应式设计，无论您使用什么设备，都能获得流畅的观影体验。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 max-w-[1000px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <h1 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-2">关于窝窝影院</h1>
          <p className="text-foreground-500 text-sm mb-10">高清播放在线不缓冲，流畅观看超清画质无广告</p>

          <section className="mb-12">
            <h2 className="font-heading text-xl text-foreground-100 mb-4">品牌介绍</h2>
            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <p className="text-foreground-300 leading-relaxed mb-4">
                窝窝影院成立于2020年，是一家专注于提供<strong>高清在线观影体验</strong>的影视平台。我们致力于为广大影迷打造一个<strong>免费、流畅、高清</strong>的在线观影环境，让每个人都能随时随地享受电影和电视剧带来的快乐。
              </p>
              <p className="text-foreground-300 leading-relaxed mb-4">
                我们的核心理念是<strong>"高清播放，在线不缓冲"</strong>。通过先进的内容分发网络（CDN）和智能视频压缩技术，窝窝影院能够在保证超清画质的同时，实现流畅无卡顿的在线播放体验。无论您使用的是电脑、平板还是手机，都能享受到影院级别的观影感受。
              </p>
              <p className="text-foreground-300 leading-relaxed">
                目前，窝窝影院已收录<strong>超过10,000部高清电影和电视剧</strong>，涵盖动作片、喜剧片、爱情片、科幻片、恐怖片、动画片、纪录片等多种类型。我们的内容团队每日更新最新资源，确保您永远不会错过任何一部好片。
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-xl text-foreground-100 mb-4">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: '张先生', rating: 5, comment: '窝窝影院真的太好了！高清画质流畅不卡顿，而且完全免费，我每天都在用。更新速度也很快，新上映的电影很快就能看到。' },
                { name: '李女士', rating: 5, comment: '之前用过很多影视网站，窝窝影院是最稳定的。超清画质无广告，观影体验特别好。分类也很清晰，找片非常方便。' },
                { name: '王同学', rating: 5, comment: '作为一个电影迷，窝窝影院完全满足了我的需求。片源丰富，更新及时，播放流畅。强烈推荐给所有喜欢看电影的朋友！' },
              ].map((review, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <div key={j} className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-primary-500 text-xs"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-300 text-sm leading-relaxed mb-3">{review.comment}</p>
                  <p className="text-foreground-500 text-xs">—— {review.name}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-xl text-foreground-100 mb-4">我们的优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-4k-line', title: '超清画质', desc: '支持1080P和4K超清画质播放，给您身临其境的观影体验。' },
                { icon: 'ri-speed-up-line', title: '流畅不缓冲', desc: '智能CDN加速技术，确保在线播放流畅无卡顿，告别缓冲等待。' },
                { icon: 'ri-forbid-2-line', title: '纯净无广告', desc: '观影过程中零广告打扰，沉浸式观看体验不打折扣。' },
                { icon: 'ri-refresh-line', title: '每日更新', desc: '专业内容团队全天候更新，新片速递、好剧不断，精彩不容错过。' },
                { icon: 'ri-money-cny-circle-line', title: '完全免费', desc: '无需注册、无需付费，海量影视资源永久免费观看。' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '电脑、平板、手机完美适配，随时随地畅享高清影视。' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-500/10 rounded-full">
                    <i className={`${item.icon} text-primary-400 text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-foreground-100 font-medium text-sm mb-1">{item.title}</h3>
                    <p className="text-foreground-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12" id="contact">
            <h2 className="font-heading text-xl text-foreground-100 mb-4">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg text-foreground-100 mb-4">联系方式</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-400"></i>
                    </div>
                    <span className="text-foreground-300 text-sm">contact@wowocinema.com</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-400"></i>
                    </div>
                    <span className="text-foreground-300 text-sm">400-888-8888</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-400"></i>
                    </div>
                    <span className="text-foreground-300 text-sm">北京市朝阳区影视产业园区A座</span>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg text-foreground-100 mb-4">在线留言</h3>
                <form
                  action="https://readdy.ai/api/form/d9ng80obdkpk99j7gj5g"
                  method="POST"
                  data-readdy-form
                  onSubmit={handleSubmit}
                >
                  <div className="space-y-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="w-full bg-background-200 text-foreground-200 text-sm px-4 py-2.5 rounded-md border border-background-300/50 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="w-full bg-background-200 text-foreground-200 text-sm px-4 py-2.5 rounded-md border border-background-300/50 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言（限500字）"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full bg-background-200 text-foreground-200 text-sm px-4 py-2.5 rounded-md border border-background-300/50 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      />
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      className="w-full bg-primary-500 text-background-950 font-medium py-2.5 rounded-md hover:bg-primary-400 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-accent-400 text-sm text-center">留言已发送，感谢您的反馈！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-accent-400 text-sm text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-xl text-foreground-100 mb-4">常见问题</h2>
            <div className="space-y-3">
              {[
                { q: '窝窝影院是否真的免费？', a: '是的，窝窝影院所有影片完全免费在线观看，无需注册、无需付费、无隐藏收费。我们依靠广告收入维持运营，为用户提供高清流畅的观影体验。' },
                { q: '窝窝影院的画质如何？', a: '窝窝影院提供超清画质播放，支持1080P和4K分辨率。我们采用先进的视频压缩和CDN加速技术，确保在线播放不缓冲、不卡顿。' },
                { q: '窝窝影院每日更新多少部影片？', a: '窝窝影院每日更新最新上映的电影和电视剧，确保用户能够第一时间观看到最新内容。我们的内容团队全天候工作，为您精选优质影视资源。' },
                { q: '如何在窝窝影院搜索想看的影片？', a: '您可以通过首页的影片分类导航浏览不同类型的内容，也可以使用搜索功能直接查找您想看的影片。我们提供了动作片、喜剧片、爱情片、科幻片等多种分类。' },
                { q: '窝窝影院支持哪些设备观看？', a: '窝窝影院支持所有主流设备，包括电脑、平板和手机。我们的网站采用响应式设计，无论您使用什么设备，都能获得流畅的观影体验。' },
              ].map((faq, i) => (
                <details key={i} className="bg-background-100 rounded-lg group">
                  <summary className="px-6 py-4 text-foreground-200 text-sm font-medium cursor-pointer list-none flex items-center justify-between">
                    {faq.q}
                    <div className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </div>
                  </summary>
                  <div className="px-6 pb-4 text-foreground-500 text-sm leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <div className="h-12"></div>
        </main>
        <Footer />
      </div>
    </>
  );
}